import { placeOffer } from "./api";
import { classifyFinanceStep, readStepId } from "./external-signing";
import type { FinanceSignPayload, OfferPayload, SingleOfferResponse } from "./types";

/**
 * The place-offer submit sequence, as a plain function.
 *
 * WHY IT EXISTS — AND WHY IT DID NOT, WHICH IS THE SAME STORY. Accept and
 * cancel already have `offer-decision.ts`, extracted for exactly this reason
 * and unit-tested. PLACING an offer was the one of the three still living
 * inline in a JSX callback, untested, and it is the one that reported
 * "Offer was not placed" for offers that were placed — visible to the user, and
 * invisible in the backend's logs, because the request had already succeeded.
 *
 * =========================================================================
 * THE BUG THIS FIXES: A SUCCESSFUL OFFER REPORTED AS A FAILURE
 * =========================================================================
 *
 * The call site read:
 *
 *     const response = await placeOffer(...)     // 200, offer created
 *     ...
 *     toast.success("Offer placed successfully")
 *     await onSuccess()                          // refetch the offers list
 *     return success(...)
 *     } catch { return failure("Offer was not placed") }
 *
 * The refetch was INSIDE the try, after the offer already existed. Anything it
 * threw — and it reaches an endpoint measured answering 500 intermittently —
 * landed in the same catch as a failed POST and produced a durable "no offer
 * was created and nothing was taken from your balance" receipt for an offer the
 * server had created. Reloading showed it, because it was there all along. The
 * user even saw the success toast first, since that fired one line earlier.
 *
 * So the rule this function enforces: ONCE THE SERVER HAS ACCEPTED THE OFFER,
 * NOTHING THAT HAPPENS AFTERWARDS MAY CHANGE THE OUTCOME. Refreshing the list
 * is a convenience; it is not evidence, and it cannot vote.
 *
 * =========================================================================
 * WHAT "PLACED" MEANS, STATED ONCE
 * =========================================================================
 *
 * A CUSTODIAL (platform-wallet) offer is not an on-chain transaction. The order
 * is built, signed and stored server-side, so:
 *
 *   `tx_hash` is null          — and that is NORMAL, not a failure. Nothing
 *                                here reads it; an absent hash has never meant
 *                                anything about whether an offer exists.
 *   `status` is "submitted"    — which is an ACTIVE offer, not a pending one.
 *                                The status is carried through untouched and is
 *                                never used to decide success.
 *   `step` is "OFFER_PLACED"   — the offer row exists. Whether the CHAIN leg
 *                                is also done is a separate question, answered
 *                                by `chain-settlement.ts` when the step is
 *                                `SUBMIT_OFFER` instead. Either way, none of that
 *                                is decided here: this function answers "does
 *                                the offer exist", and the caller asks the
 *                                chain question afterwards if it needs to.
 *
 * The identity of the offer is its `id` / `order_hash`, never a transaction
 * hash — see `readOfferId`.
 */

export type PlaceOfferOutcome =
  /**
   * The server accepted the offer and the row exists. The only outcome that
   * means anything was created.
   *
   * NOT necessarily on-chain yet: a `SUBMIT_OFFER` response lands here too, and
   * the caller is expected to ask `classifyChainPost` whether it still owes a
   * wait before it announces anything.
   */
  | {
      outcome: "placed";
      /** `data.offer.id` / `data.item.id` / `data.offer_id`, or null if none was sent. */
      offerId: number | string | null;
      response: SingleOfferResponse;
    }
  /**
   * A self-custody wallet owes signatures. NOT placed yet: the caller hands
   * off to the wallet panel and records nothing.
   */
  | {
      outcome: "sign_required";
      response: SingleOfferResponse;
      step: string;
      signPayload: FinanceSignPayload;
      offerId: number | string;
    }
  /**
   * The server asked for a signature and sent nothing to sign, or no id to
   * confirm against. Refused rather than guessed — see `MissingSignPayloadError`.
   */
  | { outcome: "unusable"; response: SingleOfferResponse }
  /** The request itself failed. The only outcome that means no offer exists. */
  | { outcome: "failure"; error: unknown };

/**
 * The offer's id, from whichever field this response carries it in.
 *
 * THREE SPELLINGS, ONE READER. The custodial create response has historically
 * used `data.item`; the documented current shape answers with `data.offer` plus
 * a sibling `data.offer_id`; the external-wallet handoff is told `offer_id`.
 * The call site read ONLY `data.item.id`, so against the documented shape the
 * receipt showed no offer id at all — a silent degradation, since a missing id
 * just omits a row.
 *
 * Order is most-specific-first. `readStepId` is reused for the flat field so
 * this agrees with the handoff about what counts as a usable id.
 */
export function readOfferId(response: SingleOfferResponse | null | undefined): number | string | null {
  const data = response?.data as
    | { offer?: { id?: unknown } | null; item?: { id?: unknown } | null }
    | undefined;

  for (const candidate of [data?.offer?.id, data?.item?.id]) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }

  return readStepId(response as never, "offer_id");
}

export type SubmitPlaceOfferInput = {
  contractAddress: string;
  tokenId: string | number;
  payload: OfferPayload;
  /**
   * Refreshes the offers list once the offer exists.
   *
   * CALLED, AWAITED, AND ITS FAILURE SWALLOWED — deliberately, and it is the
   * whole point of this function. See the banner: a refetch is not allowed to
   * turn a placed offer into a failed one. It is still awaited so the list the
   * user lands on is the new one whenever the refetch does work.
   */
  onPlaced: () => Promise<void> | void;
  /** Injection seam for tests only; production uses the real api.ts call. */
  api?: { place: typeof placeOffer };
};

export async function submitPlaceOffer({
  contractAddress,
  tokenId,
  payload,
  onPlaced,
  api = { place: placeOffer },
}: SubmitPlaceOfferInput): Promise<PlaceOfferOutcome> {
  let response: SingleOfferResponse;
  try {
    response = await api.place(contractAddress, tokenId, payload);
  } catch (error) {
    // The ONLY path to "no offer exists". Everything below this line runs with
    // an offer the server has already accepted.
    return { outcome: "failure", error };
  }

  const classification = classifyFinanceStep(response?.data?.step);

  if (classification.kind === "sign_required") {
    const signPayload = response?.data?.sign_payload;
    const offerId = readOfferId(response);
    if (!signPayload || offerId === null) return { outcome: "unusable", response };
    return {
      outcome: "sign_required",
      response,
      step: classification.step,
      signPayload,
      offerId,
    };
  }

  // `terminal` (OFFER_PLACED), `settled` (no step — the shape that predates
  // step markers) and `unknown` all land here, and lumping `unknown` in with
  // them is the deliberate part: the POST returned 2xx, so the offer exists
  // whatever the server chose to call the step, and the failure message this
  // function exists to stop saying is precisely "no offer was created".
  await refreshQuietly(onPlaced);

  return { outcome: "placed", offerId: readOfferId(response), response };
}

/**
 * Run a post-success refresh without letting it speak for what succeeded.
 *
 * EXPORTED because the external-wallet branch of the same flow needs it too:
 * once the wallet's rounds are confirmed the offer exists just as surely, and
 * a failing refetch there was reported the same wrong way.
 *
 * Reported to the console rather than swallowed in silence: a refetch that
 * keeps failing is worth knowing about — it just is not worth telling the user
 * their offer failed over.
 */
export async function refreshQuietly(fn: () => Promise<void> | void): Promise<void> {
  try {
    await fn();
  } catch (error) {
    console.error("Offer placed, but refreshing the offers list failed:", error);
  }
}
